import { IBundleExtraType, IGroup, Item } from '../graphQL/commons'
import { ICartGroup, ICartProduct, IConditionAction, IConfig } from '../interfaces'
import { sanitizeBundleExtra } from './sanitizeBundleExtra'

const itemTransformers: IConditionAction<{ source: Item; result: ICartProduct }, ICartProduct>[] = [
  {
    condition: ({ source: { layerDown, layerQty, layerUp, palletDown, palletQty, palletUp } }) => !!layerDown || !!layerQty || !!layerUp || !!palletDown || !!palletQty || !!palletUp,
    transformAction: ({ source: { layerDown, layerQty, layerUp, palletDown, palletQty, palletUp }, result }) => ({
      ...result,
      roundingRules: [
        { thresholdUp: layerUp ?? 0, value: layerQty ?? 0, thresholdDown: layerDown ?? 0, type: 'layer' },
        { thresholdUp: palletUp ?? 0, value: palletQty ?? 0, thresholdDown: palletDown ?? 0, type: 'pallet' },
      ],
    }),
  },
  {
    condition: ({ source: { jumpQty } }) => !!jumpQty,
    transformAction: ({ source: { jumpQty }, result }) => ({
      ...result,
      step: jumpQty!,
    }),
  },
  {
    condition: ({ source }) => !!source.package?.name,
    transformAction: ({ source: { sku, package: packageData}, result }) => ({
      ...result,
      id: `${sku}${packageData!.name}`,
    }),
  },
]

/**
 * Cast an Item to ICartProduct.
 * 
 * - Copies all fields from the Item to ICartProduct.
 * - Converts `jumpQty` to `step` in the result.
 * - If the Item has a package (with a name), appends the package name to the id (id = sku + package name).
 * - Adds `roundingRules` from layer and pallet information, mapping:
 *     - layerDown, layerQty, layerUp
 *     - palletDown, palletQty, palletUp
 * - All other fields from Item are passed directly unless otherwise transformed above.
 *
 * @param item {Item} - The item to cast to ICartProduct.
 * @returns {ICartProduct} The cast cart product.
 * @internal
 */
const castItemToICartProduct = (item: Item): ICartProduct => {
  const { jumpQty, layerDown, layerQty, layerUp, palletDown, palletQty, palletUp, ...rest } = item
  return itemTransformers.reduce<ICartProduct>((acc, { condition, transformAction }) => {
    const argsTransformers = { source: item, result: acc }
    return condition(argsTransformers)
      ? transformAction(argsTransformers)
      : acc
  }, {
    ...rest,
    id: rest.sku,
  })
}

/**
 * Cast array Item to array ICartProduct
 * @param source: Item[]..
 * @internal
 */
export const castItemsToICartProducts = (items: Item[]): ICartProduct[] =>
  items.map(castItemToICartProduct)

/**
 * Cast IGroup to ICartGroup
 * @param source: IGroup[]..
 * @internal
 */
export const castGroupToICartGroup = ({
  items,
  ...group
}: IGroup): ICartGroup => {
  return {
    ...group,
    items: castItemsToICartProducts(items),
  }
}

/**
 * Cast array IGroup to array ICartGroup
 * @param source: Item[]..
 * @internal
 */
export const castIGroupsToICartGroups = (groups: IGroup[]): ICartGroup[] =>
  groups.map(castGroupToICartGroup)

/**
 * Cast an Item array to it's equivalent ICartProduct array
 * @param source: Item[]
 * @param autoAdd: boolean
 * @param disableFreeGoodsAsDiscount : boolean
 * @param config : IConfig
 * @internal
 */
export const sanitizeCartProducts = (
  source: Item[],
  autoAdd: boolean,
  disableFreeGoodsAsDiscount: boolean,
  config?: IConfig,
): ICartProduct[] => {
  const freegoodsAsSubitem = !!config?.options?.freegoodsAsSubitem

  return source.map((item) => {
    const itemAsICartProduct = castItemToICartProduct(item)
    const {
      quantity,
      promotionalQty,
      discounts = 0,
      price,
    } = itemAsICartProduct
    const newQuantity = freegoodsAsSubitem
      ? quantity - promotionalQty
      : quantity
    const minQtyAllowed = autoAdd && !freegoodsAsSubitem ? promotionalQty : 0
    const newDiscounts =
      !freegoodsAsSubitem || disableFreeGoodsAsDiscount
        ? discounts
        : discounts - promotionalQty * price

    const result: ICartProduct = {
      ...itemAsICartProduct,
      quantity: newQuantity,
      inCartQuantity: quantity,
      minQtyAllowed,
      discounts: newDiscounts,
      bundlesExtra:
        itemAsICartProduct.bundlesExtra?.map((bundleExtra) => {
          return sanitizeBundleExtra({
            bundleExtra: bundleExtra as IBundleExtraType,
            bundleConfig: itemAsICartProduct.bundle,
            config,
          })
        }) || null,
    }

    return result
  })
}

/**
 * Cast an IGroup array to it's equivalent ICartGroup array
 * @param source: IGroup[]
 * @param autoAdd: boolean
 * @internal
 */
export const sanitizeCartGroups = (
  source: IGroup[],
  autoAdd: boolean,
  disableFreeGoodsAsDiscount: boolean,
  config?: IConfig,
): ICartGroup[] => {
  return source.map(({ groupBy, items, total, subtotal = 0 }) => {
    return {
      groupBy,
      total,
      subtotal,
      items: sanitizeCartProducts(
        items,
        autoAdd,
        disableFreeGoodsAsDiscount,
        config,
      ),
      visibleTotal: config?.options?.cartGroupingTotals ? total : subtotal,
    }
  })
}
